import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2, LockKeyhole, ShieldCheck } from 'lucide-react';
import { personal, residence, work, finance, loanFields, blankPerson, blankLoan } from './customerDetailsFields';

const shared = {
  current_address: 'Current address',
  current_years: 'Years at current address',
  permanent_address: 'Permanent address',
  permanent_years: 'Years at permanent address',
  office_address: 'Office address',
};

const validatePerson = (p, co = false) => {
  const errors = {};
  if ((co || !p.full_name || !p.mobile) && p.full_name.trim().length < 2) errors.full_name = 'Please enter the full name';
  if (!co || p.mobile) {
    if (!/^[6-9]\d{9}$/.test(p.mobile)) errors.mobile = 'Please enter a valid 10-digit mobile number';
  }
  if (p.pan && !/^[A-Z]{5}\d{4}[A-Z]$/.test(p.pan.toUpperCase())) errors.pan = 'PAN should look like ABCDE1234F';
  if (p.aadhaar && !/^[2-9]\d{11}$/.test(p.aadhaar)) errors.aadhaar = 'Enter a valid 12-digit Aadhaar number';
  for (const key of ['personal_email', 'official_email']) {
    if (p[key] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p[key])) errors[key] = 'Enter a valid email address';
  }
  return errors;
};

const Field = ({ field, value, onChange, error, idPrefix = '' }) => {
  const id = `${idPrefix}${field.key}`;
  return (
    <div className={field.area ? 'sm:col-span-2' : ''}>
      <label htmlFor={id} className="block text-sm font-semibold text-slate-800 mb-1.5">
        {field.label} {field.required && <span className="text-rose-600">*</span>}
      </label>
      {field.options ? (
        <select id={id} value={field.boolean ? String(value) : (value || '')}
          onChange={e => onChange(field.key, field.boolean ? e.target.value === 'true' : e.target.value)}
          className="w-full h-12 px-3 rounded-xl border border-slate-300 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-300">
          {!field.boolean && <option value="">Select if applicable</option>}
          {field.options.map(option => {
            const [key, text] = Array.isArray(option) ? option : [option, option];
            return <option key={key} value={key}>{text}</option>;
          })}
        </select>
      ) : field.area ? (
        <textarea id={id} rows={3} value={value || ''} onChange={e => onChange(field.key, e.target.value)}
          placeholder="Enter details here" aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
          className="w-full p-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-300" />
      ) : (
        <input id={id} type={field.type || 'text'} value={value || ''} onChange={e => onChange(field.key, e.target.value)}
          required={field.required} maxLength={field.maxLength} min={field.min} step={field.step}
          max={field.type === 'date' ? new Date().toISOString().slice(0, 10) : undefined}
          autoComplete={field.autoComplete} inputMode={field.type === 'tel' ? 'numeric' : undefined}
          aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
          className="w-full h-12 px-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-300" />
      )}
      {field.hint && <p className="mt-1 text-xs text-slate-500">{field.hint}</p>}
      {error && <p id={`${id}-error`} className="mt-1 text-xs text-red-700" role="alert">{error}</p>}
    </div>
  );
};

const SectionFields = ({ fields, values, onChange, errors, prefix }) => (
  <div className="grid sm:grid-cols-2 gap-5">
    {fields.filter(field => !field.when || field.when(values)).map(field =>
      <Field key={field.key} field={field} value={values[field.key]} onChange={onChange} error={errors[field.key]} idPrefix={prefix} />
    )}
  </div>
);

const sections = [
  { key: 'personal', title: 'About you', intro: 'Start with the basics. Everything except name and mobile is optional.', fields: personal },
  { key: 'residence', title: 'Where you live', intro: 'Share your current address and, if you rent, your permanent address.', fields: residence },
  { key: 'work', title: 'Work details', intro: 'Choose your work status; skip anything you do not know yet.', fields: work },
  { key: 'finance', title: 'Loans & investments', intro: 'Only add details that apply to you.', fields: finance },
  { key: 'loan', title: 'Your loan request', intro: 'Tell us what you are looking for. References are optional.', fields: loanFields },
  { key: 'review', title: 'Review & send', intro: 'Check your details before sending them securely.' },
];

const mask = value => value ? `••••${value.slice(-4)}` : 'Not provided';

const Summary = ({ title, data, fields }) => (
  <div className="rounded-xl border border-slate-200 p-4">
    <h3 className="font-bold text-slate-900 mb-3">{title}</h3>
    <dl className="grid sm:grid-cols-2 gap-3">
      {fields.filter(f => (!f.when || f.when(data)) && data[f.key] && (f.boolean ? data[f.key] : true)).map(f => (
        <div key={f.key}>
          <dt className="text-xs text-slate-500">{f.label}</dt>
          <dd className="text-sm text-slate-800 break-words">{['pan', 'aadhaar'].includes(f.key) ? mask(data[f.key]) : f.boolean ? 'Yes' : String(data[f.key])}</dd>
        </div>
      ))}
    </dl>
  </div>
);

export default function CustomerDetails() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Customer details | Finirio';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow, noarchive';
    document.head.appendChild(meta);
    return () => { meta.remove(); document.title = previousTitle; };
  }, []);
  const [applicant, setApplicant] = useState(blankPerson);
  const [coApplicant, setCoApplicant] = useState(blankPerson);
  const [sharedChecks, setSharedChecks] = useState({});
  const [hasCo, setHasCo] = useState(false);
  const [loan, setLoan] = useState(blankLoan);
  const [references, setReferences] = useState([{ name: '', mobile: '', address: '' }, { name: '', mobile: '', address: '' }]);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  const steps = hasCo
    ? [...sections.slice(0, 4).map(s => ({ ...s, person: 'applicant' })),
       ...sections.slice(0, 4).map(s => ({ ...s, person: 'coApplicant', title: `Co-applicant · ${s.title}` })),
       ...sections.slice(4)]
    : sections.map(s => ({ ...s, person: 'applicant' }));
  const current = steps[step];
  const person = current.person === 'coApplicant' ? coApplicant : applicant;
  const setPerson = current.person === 'coApplicant' ? setCoApplicant : setApplicant;
  const update = (setter, key, value) => {
    setter(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: '' }));
  };
  const move = next => { setStep(next); setError(''); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const next = () => {
    if (current.key === 'personal') {
      const found = validatePerson(person, current.person === 'coApplicant');
      if (Object.keys(found).length) { setErrors(found); return; }
    }
    if (current.key === 'loan') {
      const found = {};
      references.forEach((ref, i) => {
        if (ref.mobile && !/^[6-9]\d{9}$/.test(ref.mobile)) found[`ref${i}mobile`] = 'Enter a valid 10-digit mobile number';
      });
      if (Object.keys(found).length) { setErrors(found); return; }
    }
    setErrors({});
    move(step + 1);
  };

  const toggleShared = (key, checked) => {
    setSharedChecks(prev => ({ ...prev, [key]: checked }));
    if (checked) update(setCoApplicant, key, applicant[key]);
  };
  const updatePerson = (key, value) => {
    update(setPerson, key, value);
    if (current.person === 'coApplicant' && sharedChecks[key]) {
      setSharedChecks(prev => ({ ...prev, [key]: false }));
    }
  };

  const submit = async () => {
    if (!consent) { setError('Please agree to the privacy notice before sending.'); return; }
    setSending(true);
    setError('');
    try {
      const response = await fetch('/api/customer-details', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          applicant, co_applicant: hasCo ? coApplicant : null, loan,
          references: references.filter(r => r.name || r.mobile || r.address),
          consent,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(response.status === 422 ? 'Please check your details. PAN, Aadhaar, dates and phone numbers must be valid.' : 'Unable to save right now. Please try again.');
      setResult(data.id);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { setError(e.message || 'Could not send details. Please try again.'); }
    finally { setSending(false); }
  };

  return (
    <main className="min-h-screen bg-[#f6f8fb] py-8 md:py-14">
      <div className="max-w-[820px] mx-auto px-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <Link to="/" className="font-heading font-extrabold text-xl text-[#0b2545]">FINIRIO</Link>
          <span className="inline-flex items-center gap-2 text-xs font-medium text-slate-600"><LockKeyhole size={15} /> Private customer form</span>
        </div>
        {result ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-8 md:p-12 text-center shadow-sm">
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold text-[#0b2545]">Details received. Thank you!</h1>
            <p className="text-slate-600 mt-3">Your information has been sent to the Finirio team. Please keep this reference for your records.</p>
            <p className="mt-6 p-3 rounded-lg bg-slate-50 font-mono text-sm break-all">{result}</p>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#1e73be]">Customer information</p>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0b2545] mt-2">A few details, at your pace.</h1>
              <p className="text-slate-600 mt-2">You can skip optional questions. No documents are needed here.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-5 md:px-8 border-b border-slate-100">
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="font-semibold text-[#0b2545]">Step {step + 1} of {steps.length}</span>
                  <span className="text-slate-500">{Math.round(((step + 1) / steps.length) * 100)}% complete</span>
                </div>
                <div className="w-full h-2 rounded-full bg-slate-100" role="progressbar" aria-valuenow={step + 1} aria-valuemin={1} aria-valuemax={steps.length} aria-label="Form progress">
                  <div className="h-2 rounded-full bg-[#1e73be] transition-all" style={{ width: `${((step + 1) / steps.length) * 100}%` }} />
                </div>
              </div>
              <div className="p-5 md:p-8">
                <h2 className="font-heading text-2xl font-bold text-[#0b2545]">{current.title}</h2>
                <p className="text-slate-600 text-sm mt-1 mb-7">{current.intro}</p>
                {current.key !== 'review' && (
                  <>
                    {current.key === 'personal' && current.person === 'applicant' && (
                      <label className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl mb-6 text-sm text-slate-800">
                        <input type="checkbox" checked={hasCo} onChange={e => { setHasCo(e.target.checked); setCoApplicant(blankPerson()); setSharedChecks({}); }} className="w-5 h-5 accent-[#1e73be]" />
                        I have a co-applicant to add
                      </label>
                    )}
                    {current.person === 'coApplicant' && ['residence', 'work'].includes(current.key) && (
                      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 mb-5">
                        <p className="font-semibold text-sm text-[#0b2545] mb-2">Same as applicant?</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(shared).filter(([key]) => current.key === 'residence' ? key.includes('address') || key.includes('years') : key === 'office_address').map(([key, label]) => (
                            <label key={key} className="text-xs rounded-lg border border-blue-200 bg-white px-3 py-2 text-[#1e73be] cursor-pointer flex items-center gap-2">
                              <input type="checkbox" checked={!!sharedChecks[key]} onChange={e => toggleShared(key, e.target.checked)} className="accent-[#1e73be]" />
                              Same {label.toLowerCase()} as applicant
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-slate-600 mt-2">Copied details can be edited below. Identity details are never copied.</p>
                      </div>
                    )}
                    {current.key === 'personal' ? (
                      <>
                        <SectionFields fields={personal.slice(0, 7)} values={person} onChange={updatePerson} errors={errors} prefix={current.person} />
                        <details className="mt-6 rounded-xl border border-slate-200 p-4" open={!!(errors.pan || errors.aadhaar)}>
                          <summary className="cursor-pointer font-semibold text-[#1e73be]">Add more personal details (optional)</summary>
                          <p className="text-xs text-slate-600 my-4">Family, identification and other details can be provided now or discussed with your advisor later.</p>
                          <SectionFields fields={personal.slice(7)} values={person} onChange={updatePerson} errors={errors} prefix={current.person} />
                        </details>
                      </>
                    ) : (
                      <SectionFields fields={current.fields} values={current.key === 'loan' ? loan : person} onChange={current.key === 'loan' ? (k, v) => update(setLoan, k, v) : updatePerson} errors={errors} prefix={current.key === 'loan' ? 'loan' : current.person} />
                    )}
                    {current.key === 'loan' && (
                      <div className="mt-8 border-t border-slate-100 pt-6">
                        <h3 className="font-bold text-slate-900">References (optional)</h3>
                        <p className="text-sm text-slate-600 mt-1 mb-4">Up to two friends or colleagues. Please share their contact details only with their permission.</p>
                        {references.map((ref, index) => (
                          <div key={index} className="mb-4 p-4 rounded-xl bg-slate-50 border border-slate-100">
                            <p className="font-semibold text-sm mb-3">Reference {index + 1}</p>
                            <div className="grid sm:grid-cols-2 gap-4">
                              {[{ key: 'name', label: 'Name' }, { key: 'mobile', label: 'Mobile', type: 'tel', maxLength: 10 }, { key: 'address', label: 'Address', area: true }].map(field => (
                                <Field key={field.key} field={field} idPrefix={`ref${index}`}
                                  value={ref[field.key]} error={errors[`ref${index}${field.key}`]}
                                  onChange={(k, v) => setReferences(previous => previous.map((r, i) => i === index ? { ...r, [k]: v } : r))} />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {current.key === 'review' && (
                  <div className="space-y-4">
                    <Summary title="Applicant" data={applicant} fields={[...personal, ...residence, ...work, ...finance]} />
                    {hasCo && <Summary title="Co-applicant" data={coApplicant} fields={[...personal, ...residence, ...work, ...finance]} />}
                    <Summary title="Loan request" data={loan} fields={loanFields} />
                    {references.some(r => r.name || r.mobile || r.address) && (
                      <div className="rounded-xl border border-slate-200 p-4"><h3 className="font-bold mb-2">References</h3>
                        {references.filter(r => r.name || r.mobile || r.address).map((r, i) => <p className="text-sm text-slate-700" key={i}>{r.name || 'Name not provided'} · {r.mobile || 'No mobile'} · {r.address || 'No address'}</p>)}
                      </div>
                    )}
                    <div className="p-4 rounded-xl bg-blue-50 text-sm text-slate-700">
                      <div className="flex gap-2 items-start"><ShieldCheck className="w-5 h-5 text-[#1e73be] flex-none" />
                        <p>Finirio uses these details to review your loan enquiry. PAN and Aadhaar are optional; only authorised team admins can view submitted details. Read our <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline text-[#1e73be]">Privacy Policy</Link>.</p>
                      </div>
                      <label className="flex gap-3 mt-4 cursor-pointer"><input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="w-5 h-5 accent-[#1e73be] flex-none" />
                        I agree to share my information with Finirio for this loan enquiry.</label>
                    </div>
                  </div>
                )}
                {error && <p role="alert" className="mt-5 text-sm text-red-700 bg-red-50 p-3 rounded-lg">{error}</p>}
                <div className="flex items-center justify-between gap-3 mt-8 border-t border-slate-100 pt-6">
                  <button type="button" disabled={step === 0 || sending} onClick={() => { setErrors({}); move(step - 1); }}
                    className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 text-slate-700 disabled:opacity-40"><ArrowLeft size={17} /> Back</button>
                  {step < steps.length - 1 ? (
                    <button type="button" onClick={next} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#1e73be] text-white font-semibold hover:bg-[#155a96]">Continue <ArrowRight size={17} /></button>
                  ) : (
                    <button type="button" disabled={sending} onClick={submit} className="px-5 py-3 rounded-xl bg-[#1e73be] text-white font-semibold disabled:opacity-50">{sending ? 'Sending…' : 'Send my details'}</button>
                  )}
                </div>
              </div>
            </div>
            <p className="text-center text-xs text-slate-500 mt-6">Please do not enter passwords, bank account numbers or OTPs in this form.</p>
          </>
        )}
      </div>
    </main>
  );
}